// Third-party
import React, { ReactElement } from 'react';
import classNames from 'classnames';

// Components
import { ButtonProps } from '../buttons/Button';
import { PrimaryButton, CancelButton, DestructiveButton, WarningButton, SecondaryButton, CloseButton } from '../buttons';
import Modal from '@/views/components/modals/AccessibleModal';

// Utils
import { formatMessage } from '../../../utils/i18n';

// Types
import ModalSize from '@/types/modal-size';

export interface DefaultModalProps {
  actionsInverted?: boolean;
  backdrop?: boolean;
  backdropClassName?: string;
  body?: ReactElement | string;
  cancelLabel?: string;
  className?: string; // DIALOG CLASS NAME
  confirmLabel?: string;
  footerClassName?: string;
  hideCancelButton?: boolean;
  hidePrimaryButton?: boolean;
  isDestructive?: boolean;
  isSubmitting?: boolean;
  isV2?: boolean;
  isWarning?: boolean;
  modalClassName?: string; // MODAL CLASS NAME
  onCancel?: () => void;
  onClosed?: () => void;
  onConfirm?: () => void;
  onSecondaryAction?: () => void;
  primaryIsDisabled?: boolean;
  secondaryActionLabel?: string;
  show?: boolean;
  size?: ModalSize;
  title?: ReactElement | string;
}

const DefaultModal = (props: DefaultModalProps) => {
  const {
    actionsInverted,
    body = '',
    cancelLabel,
    className,
    confirmLabel,
    footerClassName,
    hideCancelButton,
    hidePrimaryButton,
    isDestructive,
    isSubmitting,
    isV2,
    isWarning,
    modalClassName,
    onCancel,
    onClosed,
    onConfirm = () => {},
    onSecondaryAction,
    secondaryActionLabel,
    size,
    title = '',
    ...rest
  } = props;

  const secondaryButtonClassName = isV2 ? 'secondary-v2' : '';

  return (
    <Modal
      className={modalClassName}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      dialogClassName={classNames('modal-dialog-centered', className)}
      onHide={onCancel || (() => {})}
      onExited={onClosed}
      size={size}
      {...rest}
    >
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
        {!!onCancel && <CloseButton onClick={onCancel} />}
      </Modal.Header>

      <Modal.Body>{body}</Modal.Body>

      <Modal.Footer className={footerClassName}>
        {onCancel && !hideCancelButton && (
          <CancelButton
            className={secondaryButtonClassName}
            data-testid="defaultModal-cancelButton"
            onClick={onCancel}
            disabled={isSubmitting}
            label={cancelLabel}
          />
        )}

        {!actionsInverted ? (
          <>
            {hidePrimaryButton || getPrimaryButton(props)}
            {onSecondaryAction && secondaryActionLabel && (
              <SecondaryButton className={secondaryButtonClassName} onClick={onSecondaryAction}>
                {secondaryActionLabel}
              </SecondaryButton>
            )}
          </>
        ) : (
          <>
            {onSecondaryAction && secondaryActionLabel && (
              <SecondaryButton className={secondaryButtonClassName} onClick={onSecondaryAction}>
                {secondaryActionLabel}
              </SecondaryButton>
            )}
            {hidePrimaryButton || getPrimaryButton(props)}
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const getPrimaryButton = (props: DefaultModalProps) => {
  const label = <span>{props.confirmLabel || formatMessage('Modal.Default.Confirm')}</span>;
  let OutputButton: React.ComponentType<ButtonProps> = PrimaryButton;
  let cyTag = 'primaryButton-mainAction';
  let outputButtonClassName = '';

  if (!props.isDestructive && !props.isWarning) {
    outputButtonClassName = props.isV2 ? 'primary-v2' : '';
  }

  if (props.isDestructive) {
    OutputButton = DestructiveButton;
    cyTag = 'destructiveButton-destructiveAction';
    outputButtonClassName = props.isV2 ? 'destructive-v2' : '';
  }

  if (props.isWarning) {
    OutputButton = WarningButton;
    cyTag = 'warningButton-warningAction';
    outputButtonClassName = props.isV2 ? 'warning-v2' : '';
  }

  return (
    <OutputButton
      className={outputButtonClassName}
      data-testid="defaultModal-primaryButton"
      loading={props.isSubmitting}
      onClick={props.onConfirm}
      disabled={props.primaryIsDisabled}
    >
      {label}
    </OutputButton>
  );
};

export default DefaultModal;
